"use client";

import { Activity, ArrowRight, Baby, HeartPulse, Microscope, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  icon: Stethoscope,
  title: "OTA Management",
  description: "Optimize your listings on major platforms to increase visibility and drive direct bookings effectively."
}, {
  icon: Baby,
  title: "Tech Integration",
  description: "Expert guidance on selecting and implementing the right hospitality tech stack for your property."
}, {
  icon: HeartPulse,
  title: "Revenue Audits",
  description: "Comprehensive analysis of your current revenue streams to identify growth opportunities and gaps."
}, {
  icon: Activity,
  title: "Demand Strategy",
  description: "Data-driven approaches to manage seasonal demand and maximize RevPAR throughout the year."
}, {
  icon: ShieldCheck,
  title: "Market Analysis",
  description: "In-depth competitive benchmarking to ensure your pricing remains sharp and market-relevant."
}, {
  icon: Microscope,
  title: "Process Review",
  description: "Streamlining your internal revenue operations for better efficiency and higher profit margins."
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof services[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = service.icon;
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 80}ms`
  }} className={`group relative flex flex-col rounded-2xl border border-border bg-card p-8 sm:p-10 transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-lg ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="mb-8">
        <div className="flex h-[88px] w-[88px] items-center justify-center rounded-lg bg-accent transition-colors duration-500 group-hover:bg-primary">
          <Icon className="h-[44px] w-[44px] text-primary transition-colors duration-500 group-hover:text-primary-foreground" strokeWidth={1.25} />
        </div>
      </div>

      <h3 className="font-serif text-2xl font-medium text-foreground mb-3">
        {service.title}
      </h3>

      <p className="font-default text-[15px] leading-relaxed text-muted-foreground mb-8 flex-1">
        {service.description}
      </p>

      <Link to="/ServicesPage" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent-foreground transition-colors duration-300">
        <span>View details</span>
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>;
}
export default function Services() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="668" id="resourcecategorygrid" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20 lg:mb-24 transition-all duration-700 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-6 flex flex-col">
            <span className="font-default text-xs tracking-[0.2em] uppercase text-primary mb-6">
              Our Services
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] font-medium text-foreground tracking-tight">
              Revenue growth for your hotel.
            </h2>
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-8 max-w-xl">
              We provide specialized revenue management consulting to help hotel owners and investors maximize profitability through data-driven strategies.
            </p>
          </div>

          <div className="lg:col-span-6 lg:pt-24">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              At HotelRevBaBa, we combine deep industry expertise with modern technology to solve complex demand challenges. Our hands-on approach ensures your property stays competitive and profitable.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <ServiceCard key={service.title} service={service} index={i} />)}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex justify-center">
          <Link to="/ServicesPage" className="group inline-flex items-center gap-2 font-default text-sm tracking-wide text-primary hover:text-accent-foreground transition-colors duration-300">
            <span className="border-b border-primary/30 group-hover:border-accent-foreground/50 pb-1 transition-colors duration-300">
              See all consulting services
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}